import contentfulGql from 'js/lib/contentfulGql';

const FeaturedImageFieldsFragment = contentfulGql`#graphql
  fragment FeaturedImageFields on Asset {
    description
    url(
      transform: {
        width: 330
        height: 216
        resizeStrategy: FILL
        resizeFocus: FACES
        format: JPG_PROGRESSIVE
        quality: 60
      }
    )
  }
`;

const ArticleCardFieldsFragment = contentfulGql`#graphql
  ${FeaturedImageFieldsFragment}
  fragment ArticleCardFields on Article {
    slug
    title
    intro
    localizedCountryCode
    countryCode
    featuredImage {
      ...FeaturedImageFields
    }
    sys {
      firstPublishedAt
      publishedAt
    }
  }
`;

const SeoContentCardFieldsFragment = contentfulGql`#graphql
  ${FeaturedImageFieldsFragment}
  fragment SeoContentCardFields on SeoContentContentPage {
    slug
    title
    intro
    countryCode
    featuredImage {
      ...FeaturedImageFields
    }
    sys {
      firstPublishedAt
      publishedAt
    }
  }
`;

const SeoContentHubPageBottomContentLinksFragment = contentfulGql`#graphql
  fragment SeoContentHubPageBottomContentLinks on SeoContentHubPageBottomContentLinks {
    assets {
      block {
        title
        description
        url(transform: {
          width: 1500,
          quality: 60
        })
        width
        height
        sys {
          id
        }
      }
    },
    entries {
      block {
        __typename
        sys {
          id
        }
        ... on Project {
          title
          description
        }
        ... on ArticlesHighlight {
          body {
            json
          }
        }
        ... on ArticlesVlpEmbed {
          url
          videoTimestamp
          videoDescription
          embedType
        }
        ... on Faq {
          question
          answer
        }
        ... on ArticlesDegreeRecommendations {
          productType
          category
        }
        ... on ArticlesPromoUnit {
          type
        }
        ... on ArticlesImageLink {
          link
          width
          image {
            title
            description
            url
            width
            height
            sys {
              id
            }
          }
        }
        ... on ArticlesVideoEntry {
          title
          externalEmbedVideoUrl
          previewImage {
            title
            description
            url
            contentType
            sys {
              id
            }
          }
          video {
            title
            description
            url
            contentType
            sys {
              id
            }
          }
          socialMediaEmbed
        }
      }
    }
  }
`;

const SeoContentHubNavigationLinkFieldsFragment = contentfulGql`#graphql
  fragment SeoContentHubNavigationLinkFields on SeoContentHubPage {
    title
    slug
    countryCode
    pageType
  }
`;

const SeoContentHubSubNavigationFieldsFragment = contentfulGql`#graphql
  fragment SeoContentHubSubNavigationFields on ArticlesSubcategory {
    categoryCollection (limit: 1) {
      items {
        pageDataCollection (limit: 1) {
          items {
            slug
          }
        }
      }
    }
    pageDataCollection (where: { countryCode_in: [$countryCode] }, limit: 1) {
      items {
        ...SeoContentHubNavigationLinkFields
      }
    }
  }
`;

const SeoContentHubTopLevelNavigationFieldsFragment = contentfulGql`#graphql
fragment SeoContentHubTopLevelNavigationFields on ArticlesCategory {
  pageDataCollection (where: { countryCode_in: [$countryCode] }, limit: 1) {
    items {
      ...SeoContentHubNavigationLinkFields
    }
  }
}
`;

export const SeoContentHubNavigationFieldsFragment = contentfulGql`#graphql
   ${SeoContentHubNavigationLinkFieldsFragment}
   ${SeoContentHubTopLevelNavigationFieldsFragment}
   ${SeoContentHubSubNavigationFieldsFragment}
   fragment SeoContentHubNavigationFields on SeoContentHubNavigation {
    topLevelCategoriesCollection {
      items {
        ...SeoContentHubTopLevelNavigationFields
      }
    }
    subcategoriesCollection {
      items {
        ...SeoContentHubSubNavigationFields
      }
    }
   }
`;

export const SeoContentHubPageFieldsFragment = contentfulGql`#graphql
  ${SeoContentHubPageBottomContentLinksFragment}
  ${ArticleCardFieldsFragment}
  ${FeaturedImageFieldsFragment}
  ${SeoContentHubNavigationFieldsFragment}
  ${SeoContentCardFieldsFragment}
  fragment SeoContentHubPageFields on SeoContentHubPage {
    title
    slug
    pageType
    hubPageType
    introduction
    featuredImage {
      ...FeaturedImageFields
    }
    featuredContentCollection {
      items {
        ...ArticleCardFields,
        ...SeoContentCardFields
      }
    }
    filterBy {
      __typename
      ... on ArticlesCategory {
        name
      }
      ... on ArticlesSubcategory {
        name
      }
    }
    productRecommendationTitle
    productRecsSearchUrl
    discoveryCollectionSetId
    bottomContent {
      json
      links {
        ...SeoContentHubPageBottomContentLinks
      }
    }
    navigation {
      ...SeoContentHubNavigationFields
    }
    metaTitle
    metaDescription
  }
`;

export const articleFilterQuery = contentfulGql`#graphql
${ArticleCardFieldsFragment}
query articleFilter($filters: ArticleFilter!, $articlesLimit: Int, $articlesSkip: Int, $preview: Boolean!){
  articleCollection(where: $filters, limit: $articlesLimit, skip: $articlesSkip, order: sys_firstPublishedAt_DESC, preview: $preview) {
    items {
      ...ArticleCardFields
    }
    total
  }
}
`;

export const seoContentFilterQuery = contentfulGql`#graphql
${SeoContentCardFieldsFragment}
query seoContentFilter($filters: SeoContentContentPageFilter!, $limit: Int, $skip: Int, $preview: Boolean!){
  seoContentContentPageCollection(where: $filters, limit: $limit, skip: $skip, order: sys_firstPublishedAt_DESC, preview: $preview) {
    items {
      ...SeoContentCardFields
    }
    total
  }
}
`;

const SeoContentMainHubPageFieldsFragment = contentfulGql`#graphql
  ${SeoContentHubPageBottomContentLinksFragment}
  ${ArticleCardFieldsFragment}
  ${FeaturedImageFieldsFragment}
  ${SeoContentHubNavigationFieldsFragment}
  ${SeoContentCardFieldsFragment}
  fragment SeoContentMainHubPageFields on SeoContentHubPage {
    title
    pageType
    hubPageType
    introduction
    featuredImage {
      ...FeaturedImageFields
    }
    featuredContentCollection {
      items {
        ...ArticleCardFields
        ...SeoContentCardFields
      }
    }
    bottomContent {
      json
      links {
        ...SeoContentHubPageBottomContentLinks
      }
    }
    navigation {
      ...SeoContentHubNavigationFields
    }
    metaTitle
    metaDescription
  }
`;

const SeoContentHubPageFeaturedContentFieldsFragment = contentfulGql`#graphql
  ${ArticleCardFieldsFragment}
  ${SeoContentCardFieldsFragment}
  fragment SeoContentHubPageFeaturedContentFields on SeoContentHubPage {
    title
    slug
    countryCode
    featuredContentCollection {
      items {
        ...ArticleCardFields,
        ...SeoContentCardFields
      }
    }
    filterBy {
      __typename
    }
  }
`;

export const seoContentMainHubPageQuery = contentfulGql`#graphql
  ${SeoContentMainHubPageFieldsFragment}
  ${SeoContentHubPageFeaturedContentFieldsFragment}
  query seoContentMainHubPage($pageType: String!, $preview: Boolean!, $countryCode: String!, $languageCode: String!){
    hubPageWithCatgoriesData: seoContentHubPageCollection (where: { hubPageType: "Hub page with categories", pageType: $pageType, countryCode: $countryCode, languageCode: $languageCode }, limit: 1, preview: $preview) {
      items {
        ...SeoContentMainHubPageFields
      }
    }
    featuredContentByCategoryList: seoContentHubPageCollection(where: { hubPageType: "Category-level hub page", pageType: $pageType, countryCode: $countryCode, languageCode: $languageCode }, order: [sys_publishedAt_DESC], preview: $preview) {
      items {
        ...SeoContentHubPageFeaturedContentFields
      }
    }
  }
`;

export const seoContentHubPageExistsQuery = contentfulGql`#graphql
  query seoContentHubPageExistsQuery($pageType: String!, $preview: Boolean!, $countryCode: String!, $languageCode: String!){
    hubPageWithCatgoriesData: seoContentHubPageCollection (where: { hubPageType: "Hub page with categories", pageType: $pageType, countryCode: $countryCode, languageCode: $languageCode }, limit: 1, preview: $preview) {
      total
    }
  }
`;
